import { lazy, Suspense, useCallback, useMemo, useState } from "react";
import { Route, BrowserRouter } from "react-router-dom";
import { CleanConsole } from "@eaboy/clean-console";
import { SnackbarProvider } from "notistack";

// Material UI Components
import CssBaseline from "@mui/material/CssBaseline";

import { StyledEngineProvider } from "@mui/material/styles";
import { Helmet } from "react-helmet";

import { ThemeProvider as MuiThemeProvider } from "@mui/material/styles";
import {
    ThemeProvider as StylesThemeProvider,
    StylesProvider,
} from "@mui/styles";

// My Files
import { AuthProvider, DatabaseProvider, UserProvider } from "../context";
import NavigationBar from "./navbar/NavigationBar";
import HomePage from "./home/HomePage";
import SignInPage from "./signin/SignInPage";
import SetPasswordPage from "./signin/SetPasswordPage";
import UserListPage from "./management/users/UserListPage";
import NewUserPage from "./management/users/NewUserPage";
import HubListPage from "./management/hubs/HubListPage";
// import HubNestedListPage from "./management/HubNestedListPage";
import NewHubPage from "./management/hubs/NewHubPage";
import NewLinkPage from "./files/uploading/NewLinkPage";
import FileFinderPage from "./files/viewing/finderPage/FileFinderPage";
import VideoViewerPage from "./files/viewing/viewerPages/VideoViewerPage";
import CalendarPage from "./calendar/CalendarPage";
import MessagesPage from "./messages/MessagesPage";
import PDFViewerPage from "./files/viewing/viewerPages/PDFViewerPage";
import ImageViewerPage from "./files/viewing/viewerPages/ImageViewerPage";
import AudioViewerPage from "./files/viewing/viewerPages/AudioViewerPage";
import MiscFileViewerPage from "./files/viewing/viewerPages/MiscFileViewerPage";
import LinkViewerPage from "./files/viewing/viewerPages/LinkViewerPage";
import MyAccountPage from "./myaccount/MyAccountPage";
import ParentConsentFormPage from "./management/consentforms/ParentConsentFormPage";
import UserConsentFormPage from "./management/consentforms/UserConsentFormPage";
import ClubUserConsentFormPage from "./management/consentforms/ClubUserConsentFormPage";
import ClubParentConsentFormPage from "./management/consentforms/ClubParentConsentFormPage";
import SimpleVideoAnalysisPage from "./analysis/SimpleVideoAnalysisPage";
import WatchPlaylistPage from "./playlists/WatchPlaylistPage";
import { createAppTheme } from "../theme";
import {
    ColorModeContext,
    getColorModePreference,
    saveColorModePreference,
} from "../colorMode";
import CoachEdConsentFormPage from "./management/consentforms/CoachEdConsentFormPage";
import PlaylistsAndTimelinesPage from "./playlists/PlaylistsAndTimelinesPage";
import SurveySelectPage from "./surveys/SurveySelectPage";
import SurveyEditPage from "./surveys/SurveyEditPage";
import SurveyResponsePage from "./surveys/SurveyResponsePage";
import SurveyResultsPage from "./surveys/SurveyResultsPage";
import ProjectSelectPage from "./projects/ProjectSelectPage";
import ProjectEditPage from "./projects/ProjectEditPage";
import ProjectViewPage from "./projects/ProjectViewPage";
import VideoAnnotationPage from "./annotation/VideoAnnotationPage";
import SharePage from "./sharefiles/SharePage";
import MaintenanceOverlay from "./MaintenanceOverlay";
import DesktopAccessPage from "./desktopapp/DesktopAccessPage";
import MyDesktopAppPage from "./desktopapp/MyDesktopAppPage";
import { TransferProvider } from "../transferContext";

// Local maintenance tools, off unless REACT_APP_ADMIN_TOOLS is set. Put the flag
// in .env.development.local, which react-scripts loads for `start` but never for
// `build` — .env.local would leak it into a deploy.
//
// This is a RUNTIME gate, not a compile-time one. CRA emits the flag check as
// `"true" === Object({...}).REACT_APP_ADMIN_TOOLS`, and the Object() wrapper
// defeats constant folding, so the route below survives minification and is
// simply never registered. Webpack 4 emits the lazy chunk regardless, inside or
// outside the flag check. Net effect in production: /admin is unreachable, but
// the page code still ships in an anonymous numbered chunk. Removing it from the
// build altogether needs the folder gitignored or stubbed out.
const adminToolsEnabled = process.env.REACT_APP_ADMIN_TOOLS === "true";
const AdminPage = lazy(() => import("./admin/AdminPage"));

// Stops browser opening a file if dropped on window
window.addEventListener(
    "dragover",
    function (e) {
        e.preventDefault();
    },
    false,
);
window.addEventListener(
    "drop",
    function (e) {
        e.preventDefault();
    },
    false,
);

window.fileCache = new Map();

function App() {
    CleanConsole.init(); // Silences Console
    localStorage.clear();
    sessionStorage.clear();

    const [colorMode, setColorMode] = useState(getColorModePreference);
    const theme = useMemo(() => createAppTheme(colorMode), [colorMode]);
    const updateColorMode = useCallback((mode) => {
        const nextMode = mode === "dark" ? "dark" : "light";
        saveColorModePreference(nextMode);
        setColorMode(nextMode);
    }, []);
    const colorModeContextValue = useMemo(
        () => ({ mode: colorMode, setMode: updateColorMode }),
        [colorMode, updateColorMode],
    );

    function SignIn() {
        return <SignInPage />;
    }

    function SetPassword() {
        return <SetPasswordPage />;
    }

    function Home() {
        return (
            <>
                <NavigationBar navBarTitle="" pinnedNavigation />
                <HomePage />
            </>
        );
    }

    function UserList() {
        return (
            <>
                <NavigationBar navBarTitle="| Management" />
                <UserListPage />
            </>
        );
    }

    function DesktopAccess() {
        return (
            <>
                <NavigationBar navBarTitle="| Management" />
                <DesktopAccessPage />
            </>
        );
    }

    function MyDesktopApp() {
        return (
            <>
                <NavigationBar navBarTitle="| Desktop App" />
                <MyDesktopAppPage />
            </>
        );
    }

    function HubList() {
        return (
            <>
                <NavigationBar navBarTitle="| Management" />
                <HubListPage />
                {/* <HubNestedListPage /> */}
            </>
        );
    }

    function NewUser() {
        return (
            <>
                <NavigationBar navBarTitle="| Management" />
                <NewUserPage />
            </>
        );
    }

    function Share() {
        return (
            <>
                <SharePage />
            </>
        );
    }

    // AdminPage gates itself on a single uid, and the database rules are what
    // actually protect the writes.
    function Admin() {
        return (
            <>
                <NavigationBar navBarTitle="| Admin" />
                <Suspense fallback={null}>
                    <AdminPage />
                </Suspense>
            </>
        );
    }

    function ParentConsentForm() {
        return (
            <>
                <ParentConsentFormPage />
            </>
        );
    }

    function UserConsentForm() {
        return (
            <>
                <UserConsentFormPage />
            </>
        );
    }

    function ClubParentConsentForm() {
        return (
            <>
                <ClubParentConsentFormPage />
            </>
        );
    }

    function ClubUserConsentForm() {
        return (
            <>
                <ClubUserConsentFormPage />
            </>
        );
    }

    function CoachEdConsentForm() {
        return (
            <>
                <CoachEdConsentFormPage />
            </>
        );
    }

    function NewHub() {
        return (
            <>
                <NavigationBar navBarTitle="| Management" />
                <NewHubPage />
            </>
        );
    }

    function Finder() {
        return (
            <>
                <NavigationBar navBarTitle="| Files" />
                <FileFinderPage />
            </>
        );
    }

    function AddLink() {
        return (
            <>
                <NavigationBar navBarTitle="| Files" />
                <NewLinkPage />
            </>
        );
    }

    function VideoViewer() {
        return (
            <>
                <NavigationBar navBarTitle="| Files" />
                <VideoViewerPage />
            </>
        );
    }

    function AudioViewer() {
        return (
            <>
                <NavigationBar navBarTitle="| Files" />
                <AudioViewerPage />
            </>
        );
    }

    function PDFViewer() {
        return (
            <>
                <NavigationBar navBarTitle="| Files" />
                <PDFViewerPage />
            </>
        );
    }

    function ImageViewer() {
        return (
            <>
                <NavigationBar navBarTitle="| Files" />
                <ImageViewerPage />
            </>
        );
    }

    function MiscViewer() {
        return (
            <>
                <NavigationBar navBarTitle="| Files" />
                <MiscFileViewerPage />
            </>
        );
    }

    function LinkViewer() {
        return (
            <>
                <NavigationBar navBarTitle="| Files" />
                <LinkViewerPage />
            </>
        );
    }

    function Calendar() {
        return (
            <>
                <NavigationBar navBarTitle="| Calendar" />
                <CalendarPage />
            </>
        );
    }

    function MyAccount() {
        return (
            <>
                <NavigationBar navBarTitle="| My Account" />
                <MyAccountPage />
            </>
        );
    }

    function Messages() {
        return (
            <>
                <NavigationBar navBarTitle="| Messages" />
                <MessagesPage />
            </>
        );
    }

    function SimpleAnalysis() {
        return (
            <>
                <NavigationBar navBarTitle="| Files" />
                <SimpleVideoAnalysisPage />
            </>
        );
    }

    function VideoAnnotation() {
        return (
            <>
                <NavigationBar navBarTitle="| Files" />
                <VideoAnnotationPage />
            </>
        );
    }

    function WatchPlaylist() {
        return (
            <>
                <NavigationBar navBarTitle="| Files" />
                <WatchPlaylistPage />
            </>
        );
    }

    function PlaylistsAndTimelines() {
        return (
            <>
                <NavigationBar navBarTitle="| Playlists & Timelines" />
                <PlaylistsAndTimelinesPage />
            </>
        );
    }

    function SurveySelect() {
        return (
            <>
                <NavigationBar navBarTitle="| Surveys" />
                <SurveySelectPage />
            </>
        );
    }

    function SurveyEdit() {
        return (
            <>
                <NavigationBar navBarTitle="| Surveys" />
                <SurveyEditPage />
            </>
        );
    }

    function SurveyResponse() {
        return (
            <>
                <NavigationBar navBarTitle="| Surveys" />
                <SurveyResponsePage />
            </>
        );
    }

    function SurveyAnswers() {
        return (
            <>
                <NavigationBar navBarTitle="| Surveys" />
                <SurveyResultsPage />
            </>
        );
    }

    function ProjectSelect() {
        return (
            <>
                <NavigationBar navBarTitle="| Projects" />
                <ProjectSelectPage />
            </>
        );
    }

    function ProjectEdit() {
        return (
            <>
                <NavigationBar navBarTitle="| Projects" />
                <ProjectEditPage />
            </>
        );
    }

    function ProjectView() {
        return (
            <>
                <NavigationBar navBarTitle="| Projects" />
                <ProjectViewPage />
            </>
        );
    }

    return (
        <ColorModeContext.Provider value={colorModeContextValue}>
            <MuiThemeProvider theme={theme}>
                <CssBaseline />
                <StylesProvider injectFirst>
                    <StylesThemeProvider theme={theme}>
                        <Helmet>
                            <meta charSet="utf-8" />
                            <title>Nodiant</title>
                            <meta
                                name="theme-color"
                                content={theme.palette.background.default}
                            />
                            <meta
                                name="og:description"
                                content="FAW Performance Analysis."
                            />
                            <meta
                                name="description"
                                content="FAW Performance Analysis"
                            />
                        </Helmet>
                        <BrowserRouter>
                            <AuthProvider>
                                <DatabaseProvider>
                                    <UserProvider>
                                        <TransferProvider>
                                            <StyledEngineProvider injectFirst>
                                                <SnackbarProvider maxSnack={99}>
                                                    <MaintenanceOverlay />
                                                    <Route
                                                        path="/"
                                                        exact
                                                        component={Home}
                                                    />
                                                    <Route
                                                        path="/signin"
                                                        component={SignIn}
                                                    />
                                                    <Route
                                                        path="/set-password"
                                                        component={SetPassword}
                                                    />
                                                    <Route
                                                        path="/hubs"
                                                        component={HubList}
                                                    />
                                                    <Route
                                                        path="/newhub"
                                                        component={NewHub}
                                                    />
                                                    <Route
                                                        path="/managehub"
                                                        component={NewHub}
                                                    />
                                                    <Route
                                                        path="/users"
                                                        component={UserList}
                                                    />
                                                    <Route
                                                        path="/desktop-access"
                                                        component={
                                                            DesktopAccess
                                                        }
                                                    />
                                                    <Route
                                                        path="/desktop-app"
                                                        component={MyDesktopApp}
                                                    />
                                                    <Route
                                                        path="/newuser"
                                                        component={NewUser}
                                                    />
                                                    <Route
                                                        path="/manageuser"
                                                        component={NewUser}
                                                    />
                                                    <Route
                                                        path="/calendar"
                                                        component={Calendar}
                                                    />
                                                    <Route
                                                        path="/myaccount"
                                                        component={MyAccount}
                                                    />
                                                    <Route
                                                        path="/messages"
                                                        component={Messages}
                                                    />
                                                    <Route
                                                        path="/annotation"
                                                        component={
                                                            VideoAnnotation
                                                        }
                                                    />
                                                    <Route
                                                        path="/video/:videoKey"
                                                        component={VideoViewer}
                                                    />
                                                    <Route
                                                        path="/audio/:audioKey"
                                                        component={AudioViewer}
                                                    />
                                                    <Route
                                                        path="/pdf/:pdfKey"
                                                        component={PDFViewer}
                                                    />
                                                    <Route
                                                        path="/image/:imageKey"
                                                        component={ImageViewer}
                                                    />
                                                    <Route
                                                        path="/file/:fileKey"
                                                        component={MiscViewer}
                                                    />
                                                    <Route
                                                        path="/link/:linkKey"
                                                        component={LinkViewer}
                                                    />
                                                    <Route
                                                        path="/consent"
                                                        component={
                                                            ParentConsentForm
                                                        }
                                                    />
                                                    <Route
                                                        path="/userconsent"
                                                        component={
                                                            UserConsentForm
                                                        }
                                                    />
                                                    <Route
                                                        path="/clubconsent"
                                                        component={
                                                            ClubParentConsentForm
                                                        }
                                                    />
                                                    <Route
                                                        path="/clubuserconsent"
                                                        component={
                                                            ClubUserConsentForm
                                                        }
                                                    />
                                                    <Route
                                                        path="/coachedconsent"
                                                        component={
                                                            CoachEdConsentForm
                                                        }
                                                    />
                                                    <Route
                                                        path="/addlink"
                                                        component={AddLink}
                                                    />
                                                    <Route
                                                        path="/files"
                                                        component={Finder}
                                                    />
                                                    <Route
                                                        path="/analysis"
                                                        component={
                                                            SimpleAnalysis
                                                        }
                                                    />
                                                    <Route
                                                        path="/playlist"
                                                        component={
                                                            WatchPlaylist
                                                        }
                                                    />
                                                    <Route
                                                        path="/playlists"
                                                        component={
                                                            PlaylistsAndTimelines
                                                        }
                                                    />
                                                    <Route
                                                        path="/surveys/s"
                                                        component={SurveySelect}
                                                    />
                                                    <Route
                                                        path="/surveys/e"
                                                        component={SurveyEdit}
                                                    />
                                                    <Route
                                                        path="/surveys/r"
                                                        component={
                                                            SurveyResponse
                                                        }
                                                    />
                                                    <Route
                                                        path="/surveys/a"
                                                        component={
                                                            SurveyAnswers
                                                        }
                                                    />
                                                    <Route
                                                        path="/projects/s"
                                                        component={
                                                            ProjectSelect
                                                        }
                                                    />
                                                    <Route
                                                        path="/projects/e"
                                                        component={ProjectEdit}
                                                    />
                                                    <Route
                                                        path="/projects/v"
                                                        component={ProjectView}
                                                    />
                                                    <Route
                                                        path="/share"
                                                        component={Share}
                                                    />
                                                    {adminToolsEnabled && (
                                                        <Route
                                                            path="/admin"
                                                            component={Admin}
                                                        />
                                                    )}
                                                </SnackbarProvider>
                                            </StyledEngineProvider>
                                        </TransferProvider>
                                    </UserProvider>
                                </DatabaseProvider>
                            </AuthProvider>
                        </BrowserRouter>
                    </StylesThemeProvider>
                </StylesProvider>
            </MuiThemeProvider>
        </ColorModeContext.Provider>
    );
}

export default App;
